/**
 * zustand：只存 UI 状态（specs/07 §7）。
 * 服务端实体一律走 TanStack Query，避免同一份数据在两处真相源中漂移。
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";

export type GraphMode = "structure" | "state" | "assets";
export type GenerationMode = "all" | "text" | "image" | "video";

export interface ToastItem {
  id: string;
  title: string;
  detail: string;
  mark: string;
}

export interface ModalState {
  eyebrow: string;
  title: string;
  /** 由视图决定如何渲染；这里只存标识与参数，避免把服务端数据塞进 UI store。 */
  kind: string;
  payload?: Record<string, unknown>;
}

interface UiState {
  /**
   * 当前项目 ID。空串表示尚未选定——此时项目级查询全部 disabled，
   * 由视图渲染空态并提供"新建项目"入口（specs/14 §5.2）。
   * 不再默认指向种子项目，避免掩盖"没有项目"这一真实状态。
   */
  projectId: string;
  setProjectId: (id: string) => void;

  graphMode: GraphMode;
  graphZoom: number;
  selectedNodeId: string;
  affectedNodes: string[];
  setGraphMode: (mode: GraphMode) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  resetZoom: () => void;
  selectNode: (id: string, affected?: string[]) => void;

  selectedSceneId: string;
  selectScene: (id: string) => void;

  generationMode: GenerationMode;
  setGenerationMode: (mode: GenerationMode) => void;

  discoverGenre: string;
  setDiscoverGenre: (genre: string) => void;

  modal: ModalState | null;
  openModal: (modal: ModalState) => void;
  closeModal: () => void;

  toasts: ToastItem[];
  pushToast: (toast: Omit<ToastItem, "id">) => void;
  dismissToast: (id: string) => void;
}

const ZOOM_MIN = 0.6;
const ZOOM_MAX = 1.6;
const ZOOM_STEP = 0.1;

let sequence = 0;
function nextId(prefix: string): string {
  sequence += 1;
  return `${prefix}_${Date.now().toString(36)}_${sequence}`;
}

export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      projectId: "",
      setProjectId: (projectId) => set({ projectId }),

      graphMode: "structure",
      graphZoom: 1,
      selectedNodeId: "A02",
      affectedNodes: [],
      setGraphMode: (graphMode) => set({ graphMode }),
      zoomIn: () =>
        set((state) => ({
          graphZoom: Math.min(ZOOM_MAX, Number((state.graphZoom + ZOOM_STEP).toFixed(2))),
        })),
      zoomOut: () =>
        set((state) => ({
          graphZoom: Math.max(ZOOM_MIN, Number((state.graphZoom - ZOOM_STEP).toFixed(2))),
        })),
      resetZoom: () => set({ graphZoom: 1 }),
      selectNode: (selectedNodeId, affected = []) =>
        set({ selectedNodeId, affectedNodes: affected }),

      selectedSceneId: "SC_A01_02",
      selectScene: (selectedSceneId) => set({ selectedSceneId }),

      generationMode: "all",
      setGenerationMode: (generationMode) => set({ generationMode }),

      discoverGenre: "全部",
      setDiscoverGenre: (discoverGenre) => set({ discoverGenre }),

      modal: null,
      openModal: (modal) => set({ modal }),
      closeModal: () => set({ modal: null }),

      toasts: [],
      pushToast: (toast) =>
        set((state) => ({ toasts: [...state.toasts, { ...toast, id: nextId("toast") }] })),
      dismissToast: (id) =>
        set((state) => ({ toasts: state.toasts.filter((item) => item.id !== id) })),
    }),
    {
      // 只持久化**界面状态**（当前项目 / 题材筛选），刷新不再复位。
      // 服务端实体一律不落本地（RULES §3）：它们由 react-query 从后端取。
      // ⚠️ 追更订阅**已迁出**（2026-10-06，`ADR-024`）：它是用户数据而非界面状态，
      //    曾以 `followedStories` 落 localStorage，换设备即丢——现在走 `GET /audience/follows`。
      name: "otherwise.ui",
      partialize: (state) => ({
        projectId: state.projectId,
        discoverGenre: state.discoverGenre,
      }),
    },
  ),
);
